import { Location } from '@angular/common';
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { SelectItem } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { take } from 'rxjs';
import { CanDeactivateType } from '../../../../../guards/form.guard';
import { UserAreaCreateUpdateModel } from '../../../../../models/user-area.model';
import { UserModel, UserType } from '../../../../../models/user.model';
import { AlertService } from '../../../../../services/alert.service';
import { FormService } from '../../../../../services/form.service';
import { UserAreaService } from '../../../../../services/user-area.service';
import { UserService } from '../../../../../services/user.service';
import {
  fieldRequired,
  getMaxLengthFromValidator,
  getTextAreaLength,
} from '../../../../../utils/form.utils';
import { ImageCropperDialogComponent } from '../../../../shared-components/image-cropper-dialog/image-cropper-dialog.component';

@Component({
  selector: 'app-member-area-formular',
  templateUrl: './member-area-formular.component.html',
  styleUrls: ['./member-area-formular.component.scss'],
  standalone: false,
})
export class MemberAreaFormularComponent implements OnInit {
  form = new FormGroup({
    name: new FormControl('', [Validators.required]),
    description: new FormControl('', [Validators.maxLength(500)]),
    ownerId: new FormControl<number | null>(null, [Validators.required]),
    contactEmail: new FormControl<string | null>(null, [Validators.email]),
    isPublic: new FormControl<boolean>(false),
  });
  memberAreaId: number;

  editMode: boolean = false;
  isLoading: boolean = true;
  initialFormValues: any;

  users: UserModel[] = [];
  userOptions: SelectItem[] = [];
  loadingUsers = true;

  fieldRequired = fieldRequired;
  public getTextAreaLength = getTextAreaLength;
  public getMaxLengthFromValidator = getMaxLengthFromValidator;

  constructor(
    private userAreaService: UserAreaService,
    private userService: UserService,
    private router: Router,
    private alertService: AlertService,
    private location: Location,
    private activatedRoute: ActivatedRoute,
    private formService: FormService,
    private dialog: DialogService
  ) {}

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  selectedImage: string | null = null;
  private imageFile: File | null = null;

  public async ngOnInit() {
    await this.loadUsers();
    this.setupIsPublicHandling();

    const memberAreaIdParam = this.activatedRoute.snapshot.params['areaId'];
    if (memberAreaIdParam) {
      this.editMode = true;
      this.memberAreaId = +atob(memberAreaIdParam);
      await this.getMemberArea(this.memberAreaId);
    } else {
      const currentUserId = this.userService.currentUser$.value?.id;
      if (currentUserId) {
        this.form.patchValue({ ownerId: currentUserId });
      }
    }
    this.initialFormValues = JSON.parse(JSON.stringify(this.form.value));
    this.isLoading = false;
  }

  private async loadUsers(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.userService
        .getAllUsers()
        .pipe(take(1))
        .subscribe({
          next: users => {
            users = users.filter(user => user.type === UserType.USER);
            this.users = users.sort((a, b) =>
              `${a.lastname} ${a.firstname}`.localeCompare(
                `${b.lastname} ${b.firstname}`
              )
            );
            this.userOptions = users.map(user => ({
              label: `${user.firstname} ${user.lastname} (${user.email})`,
              value: user.id,
            }));
            this.loadingUsers = false;
            resolve();
          },
          error: err => {
            this.alertService.showErrorAlert('Fehler beim Laden der Benutzer.');
            this.loadingUsers = false;
            reject(err);
          },
        });
    });
  }

  private async getMemberArea(memberAreaId: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.userAreaService.getArea(memberAreaId).subscribe({
        next: memberArea => {
          this.form.patchValue({
            name: memberArea.name,
            description: memberArea.description,
            ownerId: memberArea.owner?.id ?? null,
            contactEmail: memberArea.contactEmail ?? null,
            isPublic: memberArea.isPublic,
          });

          if (memberArea.image) {
            const url = URL.createObjectURL(memberArea.image);
            this.selectedImage = url;
          }

          resolve();
        },
        error: error => {
          console.error('Error fetching member area', error);
          this.alertService.showErrorAlert(
            'Der Mitgliederbereich konnte nicht geladen werden.'
          );
          reject();
        },
      });
    });
  }

  onSubmit() {
    if (this.form.invalid) {
      this.form.markAsTouched();
      this.alertService.showErrorAlert(
        'Es gibt noch unausgefüllte Pflichtfelder.'
      );
      return;
    }

    const isPublic = this.form.controls.isPublic.value === true;
    const payload: UserAreaCreateUpdateModel = {
      name: this.form.value.name,
      description: this.form.value.description,
      ownerId: isPublic ? undefined : this.form.controls.ownerId.value,
      contactEmail: this.form.value.contactEmail || null,
      isPublic,
    };
    this.isLoading = true;
    if (this.editMode) {
      this.updateMemberArea(payload);
    } else {
      this.createMemberArea(payload);
    }
  }

  private createMemberArea(payload: UserAreaCreateUpdateModel) {
    const createRequest$ = payload.isPublic
      ? this.userAreaService.createPublicUserArea(payload, this.imageFile)
      : this.userAreaService.createUserArea(payload, this.imageFile);

    createRequest$.pipe(take(1)).subscribe({
      next: () => {
        this.alertService.showSuccessAlert('Mitgliederbereich erstellt.');
        this.initialFormValues = JSON.parse(JSON.stringify(this.form.value));
        this.router.navigate(['../'], { relativeTo: this.activatedRoute });
      },
      error: err => {
        this.alertService.showErrorAlert(
          'Fehler beim Erstellen: ' + err.message
        );
        this.isLoading = false;
      },
    });
  }

  private setupIsPublicHandling(): void {
    const ownerControl = this.form.controls.ownerId;

    this.form.controls.isPublic.valueChanges.subscribe(isPublic => {
      if (isPublic) {
        ownerControl.setValue(null);
        ownerControl.clearValidators();
        ownerControl.disable({ emitEvent: false });
      } else {
        ownerControl.enable({ emitEvent: false });
        ownerControl.setValidators([Validators.required]);
      }
      ownerControl.updateValueAndValidity({ emitEvent: false });
    });
  }

  private updateMemberArea(payload: UserAreaCreateUpdateModel) {
    this.userAreaService
      .updateUserArea(this.memberAreaId, payload, this.imageFile)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.alertService.showSuccessAlert('Mitgliederbereich aktualisiert.');
          this.initialFormValues = JSON.parse(JSON.stringify(this.form.value));
          this.router.navigate(['../../'], { relativeTo: this.activatedRoute });
        },
        error: err => {
          this.alertService.showErrorAlert(
            'Fehler beim Aktualisieren: ' + err.message
          );
          this.isLoading = false;
        },
      });
  }

  onCancel(): void {
    this.location.back();
  }

  handleImageUpload(): void {
    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || !input.files.length) return;
    const file = input.files[0];

    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      this.alertService.showErrorAlert(
        'Ungültiger Dateityp. Bitte lade nur Bilder im Format JPEG, JPG, PNG oder WEBP hoch.'
      );
      input.value = '';
      return;
    }

    this.openImageCropperDialog(event);
  }

  private openImageCropperDialog(event: Event): void {
    const dialogRef = this.dialog.open(ImageCropperDialogComponent, {
      header: 'Bild zuschneiden',
      width: '50vw',
      contentStyle: { overflow: 'auto' },
      breakpoints: {
        '640px': '90vw',
      },
      data: {
        imageChangedEvent: event,
        aspectRatio: 1 / 1,
        roundCropper: false,
        height: 800,
        containWithinAspectRatio: true,
        onlyScaleDown: false,
        format: 'png',
      },
    });

    dialogRef.onClose.pipe(take(1)).subscribe((result: any) => {
      if (!result) {
        return;
      }

      // Case 1: cropper returns a File directly
      if (result instanceof File) {
        this.imageFile = result;
        this.selectedImage = URL.createObjectURL(result);
        return;
      }

      // Case 2: cropper returns a base64 data URL string
      if (typeof result === 'string') {
        this.selectedImage = result;
        this.imageFile = this.dataUrlToFile(result, 'member-area.png');
        return;
      }

      // Case 3: cropper wraps the value in an object (e.g. { file, dataUrl })
      if (result.file instanceof File) {
        this.imageFile = result.file;
        this.selectedImage = URL.createObjectURL(result.file);
      } else if (typeof result.dataUrl === 'string') {
        this.selectedImage = result.dataUrl;
        this.imageFile = this.dataUrlToFile(result.dataUrl, 'member-area.png');
      }
    });
  }

  private dataUrlToFile(dataUrl: string, fileName: string): File {
    const arr = dataUrl.split(',');
    const mimeMatch = arr[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : 'image/webp';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], fileName, { type: mime });
  }

  /**
   * checks if the form has unsaved changes amd asks the user if he wants to leave the page
   * @returns CanDeactivateType
   */
  public canDeactivate(): CanDeactivateType {
    if (this.isLoading) {
      return true;
    }

    return this.formService.confirmDeactivation(
      this.form.value,
      this.initialFormValues
    );
  }
}
